import isEmail from 'validator/lib/isEmail';
import { getTodaysDate } from '@frontend/design-system';
import { toAchValue, toCardValue } from './fields/payment-on-file-field/payment-field.helpers';
import { getSignedAttachmentId } from './fields/pdf-field/pdf-value';
import { parseESignValue } from './signature/esign-value';
import { DynamicFormStoreState } from './store';
import { DynamicFormField, DynamicFormSection, SchemaForm, SchemaFormField, SchemaFormFieldCondition } from './types';

export const getVirtualFieldId = (field: SchemaFormField) => field.meta.dataKey ?? field.id;

/** Normalizes a field's `options` (raw array or JSON string) into a label/value array. Shared by field inputs and review rendering. */
export const getFieldOptions = (
  options?: string | Array<{ label: string; value: string }>
): Array<{ label: string; value: string }> => {
  if (!options) return [];
  if (Array.isArray(options)) return options;

  try {
    const parsed = JSON.parse(options);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// INFO: The purpose of this function is to prevent prop drilling a new prop every time a new field comes with additional properties.
const buildFieldMeta = (field: SchemaFormField): Record<string, unknown> => {
  switch (field.meta.type) {
    case 'button':
      return { buttonType: field.button_style?.type, align: field.button_style?.align };
    case 'text-with-button':
      return { buttonLabel: field.button_meta?.label, buttonType: field.button_meta?.type };
    case 'imageUpload': {
      return { limit: field.meta.limit };
    }
    case 'pdf':
      // The schema value is the media id of the unsigned document; the field value later holds the signed copy.
      return {
        originalAttachmentId: typeof field.value === 'string' ? field.value : '',
        eSignRequired: !!field.e_sign_required,
      };
    default:
      return {};
  }
};

/** Checkbox defaults arrive as `"true"`/`"false"` strings, and any non-empty string would read as checked. */
export const decodeCheckboxValue = (value: unknown): unknown => (typeof value === 'string' ? value === 'true' : value);

export interface ConvertSchemaFormsOptions {
  /** Pre-existing values keyed by virtualFieldId. */
  values?: Record<string, unknown>;
}

export const convertSchemaFormsToDynamicFormStoreState = (
  schemaForms: SchemaForm[],
  options?: ConvertSchemaFormsOptions
): Pick<DynamicFormStoreState, 'formSections' | 'formFieldsMap' | 'conditionsMap'> => {
  const { values } = options ?? {};
  const fieldsMap: Record<string, DynamicFormField> = {};
  const conditionsMap: Record<string, SchemaFormFieldCondition> = {};

  // First pass: Create all fields
  schemaForms.forEach((schemaForm) => {
    Object.values(schemaForm.fields).forEach((field) => {
      const key = getVirtualFieldId(field);

      const existingField = fieldsMap[key];

      if (existingField) {
        // INFO: This condition only occurs when a packet contains multiple primary fields with same meta type.
        // In this case we are only merging the required flag and conditionIds since those are the only relevant
        // properties for conditional logic and validation. The rest of the field properties (label, options, etc.) will be
        // based on the first field encountered in the packet with that dataKey and hence buildFieldMeta is only run on the
        // first instance of the field.

        const mergedConditionIds = [...(existingField.sourceConditionIds ?? []), ...(field.condition_ids ?? [])];
        fieldsMap[key] = {
          ...existingField,
          required: existingField.required || field.required,
          sourceConditionIds: [...new Set(mergedConditionIds)], // Deduplicate
        };
      } else {
        const rawValue = values?.[key] ?? field.value ?? undefined;
        const value = field.meta.type === 'checkbox' ? decodeCheckboxValue(rawValue) : rawValue;

        fieldsMap[key] = {
          ...field,
          key,
          hidden: field.hidden ?? false,
          value,
          autofilled: Boolean(values?.[key]),
          sourceConditionIds: field.condition_ids ? [...field.condition_ids] : [],
          targetedByConditionIds: [],
          fieldMeta: buildFieldMeta(field),
        };
      }
    });
  });

  // Second pass: Process conditions and build conditionsMap
  schemaForms.forEach((schemaForm) => {
    (schemaForm.conditions ?? []).forEach((condition) => {
      // Normalize condition: convert field IDs to virtual field IDs
      const normalizedTerms = condition.terms
        .map((term) => {
          // If term doesn't have a field, keep it as-is (might be valid condition)
          if (!term.field) {
            return term;
          }

          // Check if field exists in schema before normalizing
          const schemaField = schemaForm.fields[term.field];
          if (!schemaField) {
            return null; // Skip terms referencing nonexistent fields
          }

          return {
            ...term,
            field: getVirtualFieldId(schemaField),
          };
        })
        .filter((term): term is NonNullable<typeof term> => term !== null);

      // Skip conditions with no valid terms
      if (normalizedTerms.length === 0) return;

      const normalizedActions = condition.actions.map((action) => ({
        ...action,
        target: schemaForm.fields[action.target] ? getVirtualFieldId(schemaForm.fields[action.target]) : action.target,
      }));

      const normalizedCondition: SchemaFormFieldCondition = {
        ...condition,
        terms: normalizedTerms,
        actions: normalizedActions,
      };

      // Store in centralized map
      conditionsMap[condition.id] = normalizedCondition;

      // Populate SOURCE fields (fields mentioned in terms)
      normalizedTerms.forEach((term) => {
        if (term.field) {
          const sourceField = fieldsMap[term.field];
          if (sourceField && !sourceField.sourceConditionIds?.includes(condition.id)) {
            sourceField.sourceConditionIds = [...(sourceField.sourceConditionIds ?? []), condition.id];
          }
        }
      });

      // Populate TARGET fields (fields mentioned in actions)
      normalizedActions.forEach((action) => {
        if (action.type === 'fields') {
          const targetField = fieldsMap[action.target];
          if (targetField && !targetField.targetedByConditionIds?.includes(condition.id)) {
            targetField.targetedByConditionIds = [...(targetField.targetedByConditionIds ?? []), condition.id];
          }
        }
      });
    });
  });

  const sections = schemaForms.flatMap((schemaForm) => {
    return schemaForm.form.sections.map<DynamicFormSection>((sectionId) => {
      const sectionInfo = schemaForm.sections[sectionId];

      const virtualFieldIds = sectionInfo.fields.map((fieldId) => getVirtualFieldId(schemaForm.fields[fieldId]));

      return {
        ...sectionInfo,
        formId: schemaForm.form.id,
        formTitle: schemaForm.form.name,
        virtualFieldIds,
      };
    });
  });

  return { formSections: sections, formFieldsMap: fieldsMap, conditionsMap };
};

export const getFieldValidator = (field: DynamicFormField, value: unknown) => {
  if (field.hidden) return undefined; // skip validation for hidden fields

  // Handle array-based fields (checklist)
  if (field.meta.type === 'checklist') {
    if (field.required && (!Array.isArray(value) || value.length === 0)) {
      return 'This field is required';
    }
    return undefined;
  }

  if (field.meta.type === 'checkbox') {
    return field.required && value !== true ? 'This field is required' : undefined;
  }

  // Handle radio field - ensure a selection is made
  if (field.meta.type === 'radio') {
    if (field.required && !value) {
      return 'This field is required';
    }
    return undefined;
  }

  if (field.meta.type === 'email' && value) {
    return isEmail(String(value)) ? undefined : 'Invalid email address';
  }

  if (field.meta.type === 'phone' && value) {
    const phoneDigits = String(value).replace(/\D/g, '');
    return phoneDigits.length >= 10 ? undefined : 'Invalid phone number';
  }

  if (field.meta.type === 'checklistCategorized' && value) {
    if (field.required && (!Array.isArray(value) || value.length === 0)) {
      return 'This field is required';
    }
  }

  // Handle string-based required validation for other fields
  if (field.required && !String(value ?? '').trim()) {
    return 'This field is required';
  }

  return undefined;
};

const asRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return {};
};

const asArray = (value: unknown): Array<unknown> | undefined => {
  if (Array.isArray(value)) {
    return value as Array<unknown>;
  }
  return [];
};

const asString = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }
  return '';
};

export const getDefaultValuesObject = (sectionFields: DynamicFormField[]): Record<string, unknown> => {
  return sectionFields.reduce<Record<string, unknown>>((acc, field) => {
    switch (field.meta.type) {
      case 'address': {
        const fieldValue = asRecord(field.value);
        acc[field.key] = {
          address1: asString(fieldValue?.address1),
          address2: asString(fieldValue?.address2),
        };
        return acc;
      }

      case 'checklist': {
        acc[field.key] = asArray(field.value);
        return acc;
      }

      case 'radio': {
        acc[field.key] = asString(field.value);
        return acc;
      }

      case 'ssn': {
        acc[field.key] = asString(field.value);
        return acc;
      }

      case 'insuranceVerification': {
        const fieldValue = asRecord(field.value);
        acc[field.key] = {
          payerId: asString(fieldValue?.payerId),
          payerName: asString(fieldValue?.payerName),
          isCustomPayer: fieldValue?.isCustomPayer === true,
          memberId: asString(fieldValue?.memberId),
          groupNo: asString(fieldValue?.groupNo),
          groupName: asString(fieldValue?.groupName),
          firstName: asString(fieldValue?.firstName),
          lastName: asString(fieldValue?.lastName),
          dob: asString(fieldValue?.dob),
          relationship: asString(fieldValue?.relationship) || 'self',
          subscriberMemberId: asString(fieldValue?.subscriberMemberId),
          subscriberFirstName: asString(fieldValue?.subscriberFirstName),
          subscriberLastName: asString(fieldValue?.subscriberLastName),
          subscriberDob: asString(fieldValue?.subscriberDob),
        };
        return acc;
      }

      case 'cardCapture': {
        const fieldValue = asRecord(field.value);

        acc[field.key] = {
          front: asString(fieldValue?.front),
          back: asString(fieldValue?.back),
        };
        return acc;
      }

      case 'cardOnFile': {
        acc[field.key] = toCardValue(field.value);
        return acc;
      }

      case 'achOnFile': {
        acc[field.key] = toAchValue(field.value);
        return acc;
      }

      case 'currentDatePopulator': {
        acc[field.key] = getTodaysDate('MM/DD/YYYY');
        return acc;
      }

      case 'categorizedChecklist': {
        acc[field.key] = asArray(field.value);
        return acc;
      }

      case 'checkbox': {
        acc[field.key] = field.value === true;
        return acc;
      }

      case 'eSign': {
        acc[field.key] = parseESignValue(field.value);
        return acc;
      }

      case 'pdf': {
        // A string value is the unsigned schema attachment (kept in fieldMeta), not a patient answer.
        acc[field.key] = { signedAttachmentId: getSignedAttachmentId(field.value) };
        return acc;
      }

      default:
        acc[field.key] = asString(field.value);
        return acc;
    }
  }, {});
};

const hasFieldValue = (v: unknown) => {
  // Check for null, undefined, or empty string
  if (v === '' || v === null || v === undefined) return false;

  // Check for empty array
  if (Array.isArray(v) && v.length === 0) return false;

  // Check for empty object (plain objects only)
  if (v && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0) return false;

  return true;
};

const toNumber = (v: unknown): number => {
  if (!v) return NaN;
  return Number(v);
};

const toString = (v: unknown): string => {
  if (v === null || v === undefined) return '';
  return String(v);
};

export const doesFieldConditionMatch = (values: Record<string, unknown>, condition: SchemaFormFieldCondition) => {
  const doesTermMatch = (term: SchemaFormFieldCondition['terms'][number]) => {
    const fieldValue = term.field && values[term.field];

    switch (term.check) {
      case 'answered':
        return hasFieldValue(fieldValue);

      case 'equals':
        // Handle boolean values by converting both to strings for consistent comparison
        if (typeof fieldValue === 'boolean') {
          return fieldValue.toString() === String(term.value);
        }

        // Handle array values (e.g., checklist fields) by checking if array includes the value
        if (Array.isArray(fieldValue)) {
          return fieldValue.includes(term.value);
        }
        // eslint-disable-next-line eqeqeq
        return fieldValue == term.value; // Use loose equality to handle string/number conversions

      case 'notEquals':
        // Handle boolean values by converting both to strings for consistent comparison
        if (typeof fieldValue === 'boolean') {
          return fieldValue.toString() !== String(term.value);
        }

        // Ensure field value does NOT match the term's comparison value
        if (Array.isArray(fieldValue)) {
          return !fieldValue.includes(term.value);
        }
        // eslint-disable-next-line eqeqeq
        return fieldValue != term.value; // Use loose inequality to handle string/number conversions

      case 'greaterThan': {
        const fieldNum = toNumber(fieldValue);
        const compareNum = toNumber(term.value);
        return fieldNum > compareNum;
      }

      case 'lessThan': {
        const fieldNum = toNumber(fieldValue);
        const compareNum = toNumber(term.value);
        return fieldNum < compareNum;
      }

      case 'greaterThanOrEqual': {
        const fieldNum = toNumber(fieldValue);
        const compareNum = toNumber(term.value);
        return fieldNum >= compareNum;
      }

      case 'lessThanOrEqual': {
        const fieldNum = toNumber(fieldValue);
        const compareNum = toNumber(term.value);
        return fieldNum <= compareNum;
      }

      case 'contains': {
        const compareStr = toString(term.value).toLowerCase();

        // Handle array values - check if any element contains the search term
        if (Array.isArray(fieldValue)) {
          return fieldValue.some((item) => toString(item).toLowerCase().includes(compareStr));
        }

        // Handle string values
        const fieldStr = toString(fieldValue).toLowerCase();
        return fieldStr.includes(compareStr);
      }

      case 'notContains': {
        const compareStr = toString(term.value).toLowerCase();

        // Handle array values - check that no element contains the search term
        if (Array.isArray(fieldValue)) {
          return fieldValue.every((item) => !toString(item).toLowerCase().includes(compareStr));
        }

        // Handle string values
        const fieldStr = toString(fieldValue).toLowerCase();
        return !fieldStr.includes(compareStr);
      }

      case 'click':
        // Click event check - typically used for button
        return true;

      default:
        // Unknown check type - log warning and return false to fail safe
        console.warn(`Unknown condition check type: ${term.check}`);
        return false;
    }
  };

  return condition.operator === 'and' ? condition.terms.every(doesTermMatch) : condition.terms.some(doesTermMatch);
};

/**
 * Determines whether a form field should be hidden based on conditional visibility rules.
 *
 * This function implements a decision matrix that evaluates show/hide conditions to determine
 * a field's visibility state. It handles complex scenarios where multiple conditions with
 * conflicting actions may apply to the same field.
 *
 * @param {Object} params - Configuration object
 * @param {string[]} [params.targetedByConditionIds=[]] - IDs of conditions targeting this field
 * @param {Record<string, SchemaFormFieldCondition>} params.conditionsMap - Centralized map of all conditions
 * @param {string} params.fieldKey - Unique identifier of the field to evaluate
 * @param {Record<string, unknown>} params.values - Current form values used to evaluate conditions
 * @param {boolean} [params.defaultHidden=false] - Default visibility when no conditions apply
 *
 * @returns {boolean} - `true` if field should be hidden, `false` if field should be visible
 *
 * @example
 * // Field with show condition when question1 is answered
 * const isHidden = getFieldHiddenState({
 *   targetedByConditionIds: ['cond1'],
 *   conditionsMap: {
 *     cond1: {
 *       id: 'cond1',
 *       operator: 'and',
 *       terms: [{ field: 'question1', check: 'answered' }],
 *       actions: [{ type: 'fields', target: 'question2', operation: 'show' }]
 *     }
 *   },
 *   fieldKey: 'question2',
 *   values: { question1: 'Yes' },
 *   defaultHidden: true
 * });
 * // Returns: false (visible, because show condition is met)
 *
 * @description
 * Decision Matrix (18 possible cases):
 *
 * | Show Actions | Hide Actions | Show Matched | Hide Matched | Result  | Reason                              |
 * |--------------|--------------|--------------|--------------|---------|-------------------------------------|
 * | No           | No           | -            | -            | Default | No relevant conditions              |
 * | Yes          | No           | Yes          | -            | Visible | Show condition met                  |
 * | Yes          | No           | No           | -            | Hidden  | Show exists but not met             |
 * | No           | Yes          | -            | Yes          | Hidden  | Hide condition met                  |
 * | No           | Yes          | -            | No           | Default | Hide exists but not met             |
 * | Yes          | Yes          | Yes          | Yes          | Visible | Show wins when both match           |
 * | Yes          | Yes          | Yes          | No           | Visible | Show matched                        |
 * | Yes          | Yes          | No           | Yes          | Hidden  | Hide matched                        |
 * | Yes          | Yes          | No           | No           | Visible | Both exist, neither matched         |
 *
 * Key Rules:
 * 1. Show conditions take precedence over hide conditions when both match
 * 2. When only show actions exist and none match, field stays hidden
 * 3. When only hide actions exist and none match, field uses default state
 * 4. When both action types exist but neither matches, field is visible
 */
export const getFieldHiddenState = ({
  targetedByConditionIds = [],
  conditionsMap,
  fieldKey,
  values,
  defaultHidden = false,
}: {
  targetedByConditionIds?: string[];
  conditionsMap: Record<string, SchemaFormFieldCondition>;
  fieldKey: string;
  values: Record<string, unknown>;
  defaultHidden?: boolean;
}): boolean => {
  // TODO: add support for section-level conditions as well (actions.type === 'sections')

  // Resolve condition IDs to actual conditions
  const conditions = targetedByConditionIds
    .map((id) => conditionsMap[id])
    .filter((condition): condition is SchemaFormFieldCondition => condition !== undefined);

  // Early exit: No conditions means use default state
  if (conditions.length === 0) return defaultHidden;

  // ============================================================================
  // STEP 1: Inventory - Determine what action types exist for this field
  // ============================================================================
  // Check if any condition has a "show" action targeting this field
  const hasAnyShowActionForField = conditions.some((condition) =>
    condition.actions.some(
      (action) => action.type === 'fields' && action.target === fieldKey && action.operation === 'show'
    )
  );

  // Check if any condition has a "hide" action targeting this field
  const hasAnyHideActionForField = conditions.some((condition) =>
    condition.actions.some(
      (action) => action.type === 'fields' && action.target === fieldKey && action.operation === 'hide'
    )
  );

  // Early exit: No relevant field-level actions for this field
  // This field isn't referenced in any conditions, so treat as if no conditions exist
  if (!hasAnyShowActionForField && !hasAnyHideActionForField) {
    return defaultHidden;
  }

  // ============================================================================
  // STEP 2: Evaluation - Check which conditions currently match
  // ============================================================================
  // A "matching show condition" is one where:
  // 1. The condition's terms evaluate to true (based on current form values)
  // 2. The condition has a show action targeting this field
  const hasMatchingShowCondition = conditions.some(
    (condition) =>
      doesFieldConditionMatch(values, condition) &&
      condition.actions.some(
        (action) => action.type === 'fields' && action.target === fieldKey && action.operation === 'show'
      )
  );

  // A "matching hide condition" is one where:
  // 1. The condition's terms evaluate to true (based on current form values)
  // 2. The condition has a hide action targeting this field
  const hasMatchingHideCondition = conditions.some(
    (condition) =>
      doesFieldConditionMatch(values, condition) &&
      condition.actions.some(
        (action) => action.type === 'fields' && action.target === fieldKey && action.operation === 'hide'
      )
  );

  // ============================================================================
  // STEP 3: Decision - Apply visibility rules based on matched conditions
  // ============================================================================

  // RULE 1: Show takes precedence over hide when both conditions match
  // Example: "Show if Q1=Yes" AND "Hide if Q2=No" both match → Field is VISIBLE
  if (hasMatchingShowCondition) {
    return false; // visible
  }

  // RULE 2: Hide condition matched (and show didn't match or doesn't exist)
  // Example: "Hide if Q1=No" matches → Field is HIDDEN
  if (hasMatchingHideCondition) {
    return true; // hidden
  }

  // RULE 3: Only show actions exist, but none matched
  // Field should stay hidden until the show condition is met
  // Example: "Show if Q1=Yes" exists but Q1="" → Field stays HIDDEN
  if (hasAnyShowActionForField && !hasAnyHideActionForField) {
    return true; // hidden
  }

  // RULE 4: Only hide actions exist, but none matched
  // Field uses its default state since the hide condition isn't active
  // Example: "Hide if Q1=Yes" exists but Q1="No" → Use default state
  if (!hasAnyShowActionForField && hasAnyHideActionForField) {
    return defaultHidden;
  }

  // RULE 5: Both action types exist, but neither matched
  // Field is visible by default when conditions exist but none are satisfied
  // Example: "Show if Q1=Yes" and "Hide if Q2=Yes" exist but neither matches → Field is VISIBLE
  return false;
};

/**
 * Execute update actions from conditions triggered by a field (e.g., button click)
 *
 * @param sourceConditionIds - IDs of conditions triggered BY this field
 * @param conditionsMap - Centralized map of all conditions
 * @param values - Current form values to evaluate conditions against
 * @returns Record of field updates to apply (fieldKey -> new value)
 *
 * @example
 * // Button has conditions that update other fields when clicked
 * const updates = executeUpdateActions({
 *   sourceConditionIds: ['cond1'],
 *   conditionsMap: {
 *     cond1: {
 *       id: 'cond1',
 *       operator: 'and',
 *       terms: [{ field: 'myButton', check: 'click' }],
 *       actions: [{ operation: 'update', target: 'field1', type: 'fields', value: 'Updated!' }]
 *     }
 *   },
 *   values: { myButton: true }
 * });
 * // Returns: { field1: 'Updated!' }
 */
export const executeUpdateActions = ({
  sourceConditionIds = [],
  conditionsMap,
  values,
}: {
  sourceConditionIds?: string[];
  conditionsMap: Record<string, SchemaFormFieldCondition>;
  values: Record<string, unknown>;
}): Record<string, unknown> => {
  const updates: Record<string, unknown> = {};

  // Resolve condition IDs to actual conditions
  const conditions = sourceConditionIds
    .map((id) => conditionsMap[id])
    .filter((condition): condition is SchemaFormFieldCondition => condition !== undefined);

  // Evaluate each condition and collect update actions
  conditions.forEach((condition) => {
    // Only execute if condition matches current values
    if (doesFieldConditionMatch(values, condition)) {
      condition.actions.forEach((action) => {
        if (action.operation === 'update' && action.type === 'fields') {
          // Extract value from action
          if (action.value !== undefined) {
            updates[action.target] = action.value;
          }
        }
      });
    }
  });

  return updates;
};
